/**
 * Clips API
 * 
 * Exposes functions to interact with the /clips endpoint:
 *   1) listClips (GET /clips) - List all clips (with optional query params)
 *   2) getClipById (GET /clips/{clipId}) - Fetch a single clip by its ID
 *   3) createClip (POST /clips) - Create a new clip
 *   4) publishClip (PUT /clips/{clipId}/publish) - Publish a clip
 *   5) getClipsByStreamId (GET /clips/stream/{streamId}) - List all clips for a specific stream
 *   6) updateClipById (PUT /clips/{clipId}) - Update a clip's attributes (and regenerate VOD if timing changes)
 *   7) deleteClip (DELETE /clips/{clipId}) - Delete a clip and all its associated files
 *   8) getPresignedUploadUrl (POST /clips/upload/presigned-url) - Get URL for direct S3 upload
 *   9) uploadClip (POST /clips/upload) - Finalize an upload by providing metadata and s3Url
 *  10) getPendingOpusClipProjects (GET /clips/stream/{streamId}/generating) - Fetch pending OpusClip projects for a stream
 */

import { fetchWrapperPortal } from "./fetch";

/**
 * List all clips, optionally filtered by query parameters
 * @param {Object} [params] - An object for query parameters (e.g. { stream_id: 'stream123', status: 'pending' })
 * @returns {Promise<any>} - A Promise that resolves to the response data
 */
export const listClips = async (params = {}) => {
  // Handle sort parameter separately since it's JSON
  const { sort, ...otherParams } = params;
  const queryParams = new URLSearchParams(otherParams);
  if (sort) {
    queryParams.append('sort', sort);
  }
  
  return fetchWrapperPortal({
    url: `clips${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
    method: "GET",
    addBearer: true,
  });
};

/**
 * Get details for a specific clip by its ID
 * @param {string} clipId - The ID of the clip to retrieve
 * @returns {Promise<any>} - A Promise that resolves to the response data
 */
export const getClipById = async (clipId) =>
  fetchWrapperPortal({
    url: `clips/${clipId}`,
    method: "GET",
    addBearer: true,
  });

/**
 * Create a new clip
 * @param {Object} clipData - The details required to create a clip (e.g. { streamId, title, status, startTime, endTime })
 * @returns {Promise<any>} - A Promise that resolves to the newly created clip data
 */
export const createClip = async (clipData) => {
  console.log('Creating clip with data:', {
    ...clipData,
    startTime: Number(clipData.startTime),
    endTime: Number(clipData.endTime),
    duration: Number(clipData.endTime) - Number(clipData.startTime)
  });
  return fetchWrapperPortal({
    url: `clips`,
    method: "POST",
    addBearer: true,
    body: JSON.stringify(clipData),
  });
};

/**
 * Publish a clip by its ID
 * @param {string} clipId - The ID of the clip to publish
 * @returns {Promise<any>} - A Promise that resolves to the updated clip data
 */
export const publishClip = async (clipId) =>
  fetchWrapperPortal({
    url: `clips/${clipId}/publish`,
    method: "PUT",
    addBearer: true,
  });

/**
 * Retrieve all clips associated with a specific stream
 * @param {string} streamId - The ID of the stream to fetch clips for
 * @returns {Promise<any>} - A Promise that resolves to an array of clip documents
 */
export const getClipsByStreamId = async (streamId) =>
  fetchWrapperPortal({
    url: `clips/stream/${streamId}`,
    method: "GET",
    addBearer: true,
  });

/**
 * Update a clip's attributes and regenerate VOD if timing values change
 * @param {string} clipId - The ID of the clip to update
 * @param {Object} clipData - Fields to update (e.g. { title, startTime, endTime, ... })
 * @returns {Promise<any>} - A Promise that resolves to the updated clip document
 */
export const updateClip = async (clipId, clipData) =>
  fetchWrapperPortal({
    url: `clips/${clipId}`,
    method: "PUT",
    addBearer: true,
    body: JSON.stringify(clipData),
  });

/**
 * Delete a clip and all its associated files
 * @param {string} clipId - The ID of the clip to delete
 * @returns {Promise<any>} - A Promise that resolves to a success message or an error
 */
export const deleteClip = async (clipId) =>
  fetchWrapperPortal({
    url: `clips/${clipId}`,
    method: "DELETE",
    addBearer: true,
  });

export const generateAIClips = async (streamId, options = {}) => {
  const {
    clipType = 'opusclip',
    minDuration = 30,
    maxDuration = 180,
    maxClips = 5
  } = options;
  const params = new URLSearchParams();
  if (clipType) params.append('clip_type', clipType);
  if (minDuration) params.append('minDuration', minDuration);
  if (maxDuration) params.append('maxDuration', maxDuration);
  if (maxClips) params.append('maxClips', maxClips);
  return fetchWrapperPortal({
    url: `clips/generate/${streamId}?${params.toString()}`,
    method: "GET",
    addBearer: true,
  });
}

export const setClipParameters = async (streamId, parameters) =>
  fetchWrapperPortal({
    url: `clips/${streamId}/parameters`,
    method: "POST",
    addBearer: true,
    body: JSON.stringify(parameters),
  });

/**
 * Get a presigned URL for direct S3 clip upload.
 * @param {string} filename - The name of the file to be uploaded.
 * @param {string} contentType - The MIME type of the file (e.g., 'video/mp4').
 * @returns {Promise<{presignedUrl: string, s3Url: string}>} - A Promise resolving to the URLs.
 */
export const getPresignedUploadUrl = async (filename, contentType) => {
  return fetchWrapperPortal({
    url: `clips/upload/presigned-url`,
    method: "POST",
    addBearer: true,
    body: JSON.stringify({ filename, contentType }),
    // No isFormData: true here, this request sends JSON
  });
};

/**
 * Finalize a clip upload after the file is in S3.
 * @param {object} clipData - Metadata including { title, tags, s3Url }.
 * @returns {Promise<any>} - A Promise that resolves to the created clip data.
 */
export const uploadClip = async (clipData) => {
  return fetchWrapperPortal({
    url: `clips/upload`, // Hits the same finalization endpoint
    method: "POST",
    addBearer: true,
    body: JSON.stringify(clipData), // Send JSON metadata, including the s3Url
    // isFormData: false (or omitted), as we are sending JSON now
  });
};

/**
 * Fetch pending OpusClip projects for a specific stream
 * @param {string} streamId - The ID of the stream to fetch pending OpusClip projects for
 * @returns {Promise<any>} - A Promise that resolves to an array of pending OpusClip project documents
 */
export const getPendingOpusClipProjects = async (streamId) =>
  fetchWrapperPortal({
    url: `clips/stream/${streamId}/generating`,
    method: "GET",
    addBearer: true,
  });

/**
 * Fetch OpusClip brand templates
 * @returns {Promise<any>} - A Promise that resolves to an array of brand template objects
 */
export const getOpusClipBrandTemplates = async () =>
  fetchWrapperPortal({
    url: `clips/opusclip/brand-templates`,
    method: "GET",
    addBearer: true,
  });
